// src/components/ModalPortalAPI.js
import React, { useState, useEffect } from 'react';
import { 
  FaLock, FaTimes, FaShieldAlt, FaCheckCircle, 
  FaCopy, FaEye, FaEyeSlash, FaKey, FaSync
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../services/api';

export default function ModalPortalAPI({ isOpen, onClose, seguradoras, getAuthConfig, onRefresh }) {
  const [selectedSegId, setSelectedSegId] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  
  // Reseta os estados sempre que o modal abre ou muda de seguradora
  useEffect(() => {
    setIsRevealed(false);
  }, [selectedSegId, isOpen]);

  if (!isOpen) return null;

  const selectedSeg = seguradoras.find(s => String(s.id) === String(selectedSegId));
  const currentKey = selectedSeg?.webhook_secret || null;

  const handleGenerateKey = async () => {
    if (!selectedSegId) return toast.warn("Selecione um parceiro.");
    
    if (currentKey) {
      const confirm = window.confirm("ATENÇÃO: Gerar uma nova chave invalidará a chave atual imediatamente. A seguradora perderá o acesso até atualizar a nova chave. Deseja continuar?");
      if (!confirm) return;
    }

    setLoading(true);
    try {
      const newKey = `sk_live_${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`;
      
      await api.put(`/api/integracoes/seguradoras/${selectedSegId}`, {
        webhook_secret: newKey,
        tipo_auth: 'api_key'
      }, getAuthConfig());

      toast.success("Nova chave gerada e ativada com sucesso!");
      setIsRevealed(true); // Mostra a chave recém-gerada
      if (onRefresh) await onRefresh(); // Atualiza a lista do pai
    } catch (err) {
      toast.error("Falha ao gerar chave.");
    } finally {
      setLoading(false);
    }
  };

  const copiarChave = () => {
    if (!currentKey) return;
    navigator.clipboard.writeText(currentKey);
    toast.success("Chave copiada para a área de transferência!");
  };

  const renderMaskedKey = (key) => {
    if (!key) return 'Nenhuma chave configurada';
    if (isRevealed) return key;
    
    if (key.startsWith('sk_live_')) {
      return 'sk_live_' + '•'.repeat(key.length - 8);
    }
    return '•'.repeat(key.length);
  };

  return (
    <div className="api-modal-overlay">
      <div className="api-modal" onClick={e => e.stopPropagation()}>
        <div className="api-modal-header">
          <div className="header-title">
            <FaLock color="#0ea5e9" style={{ marginRight: '8px' }} />
            <h3>Gerenciar Credenciais</h3>
          </div>
          <button className="close-btn" onClick={() => { setSelectedSegId(''); onClose(); }}>
            <FaTimes />
          </button>
        </div>
        
        <div className="api-modal-body">
          <p style={{ color: '#475569', fontSize: '14px', marginBottom: '20px' }}>
            Selecione um parceiro configurado para visualizar ou rotacionar a chave de API (Inbound).
          </p>
          
          <div className="input-group">
            <label>Selecione a Seguradora / Parceiro</label>
            <select 
              value={selectedSegId} 
              onChange={(e) => setSelectedSegId(e.target.value)}
            >
              <option value="">-- Selecionar Parceiro --</option>
              {seguradoras.map(s => (
                <option key={s.id} value={s.id}>{s.nome} ({s.ambiente})</option>
              ))}
            </select>
          </div>

          {selectedSeg && (
            <div style={{ marginTop: '24px', animation: 'fadeIn 0.3s ease' }}>
              
              {/* Painel da Chave Atual estilo Stripe */}
              <div style={{
                background: '#0f172a',
                borderRadius: '12px',
                padding: '20px',
                color: '#f8fafc',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700 }}>
                    Chave Secreta (Webhook)
                  </span>
                  {currentKey ? (
                    <span style={{ fontSize: '10px', background: 'rgba(16, 185, 129, 0.2)', color: '#10b981', padding: '4px 10px', borderRadius: '99px', fontWeight: 800 }}>
                      <FaCheckCircle style={{ marginRight: '4px', marginBottom: '-2px' }}/> Ativa
                    </span>
                  ) : (
                    <span style={{ fontSize: '10px', background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', padding: '4px 10px', borderRadius: '99px', fontWeight: 800 }}>
                      Inativa
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', padding: '12px 16px', borderRadius: '8px', border: '1px solid #334155' }}>
                  <code style={{ 
                    fontFamily: "'JetBrains Mono', monospace", 
                    fontSize: '14px', 
                    color: currentKey ? '#38bdf8' : '#64748b',
                    letterSpacing: isRevealed ? 'normal' : '2px'
                  }}>
                    {renderMaskedKey(currentKey)}
                  </code>
                  
                  {currentKey && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        onClick={() => setIsRevealed(!isRevealed)}
                        title={isRevealed ? "Ocultar chave" : "Revelar chave"}
                        style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}
                      >
                        {isRevealed ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                      </button>
                      <button 
                        onClick={copiarChave}
                        title="Copiar chave"
                        style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}
                      >
                        <FaCopy size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="api-warning" style={{ marginTop: '20px' }}>
                <FaShieldAlt style={{ marginRight: '8px', fontSize: '20px', flexShrink: 0 }} />
                <span>
                  <strong>Precisa rotacionar a chave?</strong> Ao gerar uma nova, a anterior será invalidada. 
                  Certifique-se de atualizar a integração no painel do parceiro logo em seguida.
                </span>
              </div>

              <button 
                className="generate-btn" 
                onClick={handleGenerateKey}
                disabled={loading}
                style={{
                  background: currentKey ? '#ef4444' : '#0ea5e9', // Vermelho se for rotacionar (perigo), Azul se for nova
                  boxShadow: currentKey ? '0 4px 14px rgba(239, 68, 68, 0.4)' : '0 4px 14px rgba(14, 165, 233, 0.4)'
                }}
              >
                {loading ? <FaSync className="spin" style={{ marginRight: '8px' }} /> : <FaKey style={{ marginRight: '8px' }} />}
                {loading ? 'Processando...' : currentKey ? 'Revogar e Gerar Nova Chave' : 'Gerar Primeira Chave sk_live'}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}